import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "@/contexts/PermissionsContext";
import { useHiddenFlags, useWorkflowFlags } from "@/contexts/FeatureFlagContext";
import { isPathVisibleByFlags } from "@/lib/navFeatureFlags";
import { isPathVisibleByWorkflowFlags } from "@/lib/workflowFeatureFlags";
import { canAccessRoute, findFirstAccessiblePath } from "@/lib/rbacRoutes";

export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const { hasPermission, hasAnyAccess, loading } = usePermissions();
  const hiddenFlags = useHiddenFlags();
  const workflowFlags = useWorkflowFlags();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">
        Loading access…
      </div>
    );
  }

  if (!hasAnyAccess) {
    return (
      <Navigate
        to="/pending-access"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!isPathVisibleByFlags(location.pathname, hiddenFlags)) {
    const fallback = findFirstAccessiblePath(hasPermission, hiddenFlags);
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <Navigate
        to="/access-denied"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!isPathVisibleByWorkflowFlags(location.pathname, workflowFlags)) {
    const fallback = findFirstAccessiblePath(hasPermission, hiddenFlags);
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <Navigate
        to="/access-denied"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!canAccessRoute(location.pathname, hasPermission)) {
    const fallback = findFirstAccessiblePath(hasPermission, hiddenFlags);
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <Navigate
        to="/access-denied"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return children;
}
